import {useCallback, useEffect, useMemo, useState} from "react";
import useListRecentGames from "@/hooks/games/useListRecentGames.hook";

export type SortOption = "popular" | "recent" | "az" | "favorites";

export interface GameItem {
  id: string;
  name: string;
  provider: string;
  icon: string;
  class: string;
  isFav: boolean;
}

interface RawRecentGame {
  game_id: string;
}

const PAGE_SIZE = 24;

const useGamesCatalog = (allGames: GameItem[]) => {
  const {listRecentGames} = useListRecentGames() as {listRecentGames: RawRecentGame[] | null | undefined};

  const [search, setSearch] = useState("");
  const [provider, setProvider] = useState("");
  const [category, setCategory] = useState("");
  const [sort, setSort] = useState<SortOption>("popular");
  const [page, setPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Reset pagination whenever filters or source list change
  useEffect(() => {
    setPage(1);
  }, [allGames, search, provider, category, sort]);

  const recentIds = useMemo<ReadonlySet<string>>(() => {
    if (!Array.isArray(listRecentGames)) return new Set<string>();
    return new Set(listRecentGames.map(g => g.game_id));
  }, [listRecentGames]);

  const filtered = useMemo<GameItem[]>(() => {
    let result = allGames;
    if (search.trim()) {
      const term = search.toLowerCase();
      result = result.filter(g => g.name.toLowerCase().includes(term));
    }
    if (provider) result = result.filter(g => g.provider === provider);
    if (category) result = result.filter(g => g.class === category);
    if (sort === "favorites") result = result.filter(g => g.isFav);
    return result;
  }, [allGames, search, provider, category, sort]);

  const sorted = useMemo<GameItem[]>(() => {
    if (sort === "az") {
      return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
    }
    if (sort === "recent") {
      const recent = filtered.filter(g => recentIds.has(g.id));
      const rest = filtered.filter(g => !recentIds.has(g.id));
      return [...recent, ...rest];
    }
    return filtered;
  }, [filtered, sort, recentIds]);

  const displayedGames = useMemo<GameItem[]>(() => sorted.slice(0, page * PAGE_SIZE), [sorted, page]);

  const hasMore = displayedGames.length < sorted.length;

  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    setTimeout(() => {
      setPage(p => p + 1);
      setIsLoadingMore(false);
    }, 400);
  }, [isLoadingMore, hasMore]);

  const clearFilters = useCallback(() => {
    setSearch("");
    setProvider("");
    setCategory("");
    setSort("popular");
  }, []);

  const hasActiveFilters = search !== "" || provider !== "" || category !== "" || sort !== "popular";

  return {
    search,
    setSearch,
    provider,
    setProvider,
    category,
    setCategory,
    sort,
    setSort,
    displayedGames,
    totalCount: sorted.length,
    showingCount: displayedGames.length,
    hasMore,
    loadMore,
    isLoadingMore,
    clearFilters,
    hasActiveFilters,
  };
};

export default useGamesCatalog;
